// NPM Imports
import axios from "axios";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/browser";

// Methods Imports
import { getRedirectUrlPostSignout } from "../actions/AuthActions/authMethods";

// Internal Imports
import { GlobalUtils } from "../utils";
import { API_ENDPOINTS } from "../constants";
import store from "../store";

// Destructuring
const { APIS } = API_ENDPOINTS;
const {
  readDataFromLocalStorage,
  removeDataFromLocalStorage,
  writeDataToLocalStorage,
  setCookie,
  removeCookie,
} = GlobalUtils;

// Declarations
let fetchTokenPromise = null;
let PLAN_HASH = readDataFromLocalStorage("planHash") || null;
let APP_VERSION = null;

const removeAccessToken = () => {
  removeDataFromLocalStorage("revvsalesUser");
  removeCookie("user");
  window.location.href = getRedirectUrlPostSignout();
};

const makeCommonHeader = () => {
  const obj = {};
  obj["Content-Type"] = "application/json";
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  const SF_SAML_ORG_ID = readDataFromLocalStorage("SFSAMLOrgID"); // In case of SF SAML Auth Request
  const SIGNUP_ORG_ID = readDataFromLocalStorage("signupOrgID"); // In case of Invute Signup

  // Set the header if user is authenticated
  if (CURRENT_USER) {
    if (CURRENT_USER.access_token) {
      obj.AccessToken = CURRENT_USER.access_token;
    }
    if (CURRENT_USER.org_id) {
      obj.AccNo = CURRENT_USER.org_id;
      obj.orgID = CURRENT_USER.org_id;
    }
  }
  // Set the header if user is not authenticated
  // ex: for signup purpose or SFSAML Auth Request
  else if (SIGNUP_ORG_ID) {
    obj.AccNo = SIGNUP_ORG_ID;
    obj.orgID = SIGNUP_ORG_ID;
  } else if (SF_SAML_ORG_ID) {
    obj.AccNo = SF_SAML_ORG_ID;
    obj.orgID = SF_SAML_ORG_ID;
  }
  return obj;
};

const triggerSentryError = ({ error, level, tag }) => {
  Sentry.configureScope(scope => {
    const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
    scope.setLevel(level);
    scope.setTag("custom_tag", tag);
    scope.setExtra(
      "user-email",
      (CURRENT_USER && CURRENT_USER.user_email) || "",
    );
    scope.setExtra("app_version", process.env.REACT_APP_VERSION);
    scope.setExtra(
      "incident-id",
      (error.response &&
        error.response.data &&
        error.response.data.incident_id) ||
        "",
    );
    scope.setExtra("payload", JSON.stringify(error));
    Sentry.captureException(error);
  });
};

/**
 * @param {string} method - Holds the http request methods like get,put,etc.
 * @param {string} url - API url to hit.
 * @param {object} headers - Holds header params for the API.
 * @param {object} requestBody - Holds the payload for the API.
 * @param {string} responseType - Holds request response type, i.e for export API the response type should be "arraybuffer" to handle the response.
 * @param {boolean} returnFullResponse - A flag to decide wheather full response should be sent or only the response.data.
 */

const makeApiCall = (
  method,
  url,
  headers,
  requestBody,
  responseType,
  returnFullResponse,
) => {
  const request = {
    method,
    url,
    headers,
    data: requestBody,
  };
  if (responseType) request.responseType = responseType;

  axiosRetry(axios, {
    retries: 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: err => {
      if (
        !err.response ||
        err.code === "ECONNABORTED" ||
        (err.response && err.response.status === 0)
      ) {
        console.error("axios retry attempt =>", err.config["axios-retry"]);
        return true;
      }
      return false;
    },
  });

  return axios(request)
    .then(response => {
      // * METHOD TO PERFORM ACTIONS BASED ON API HEADERS
      // eslint-disable-next-line no-use-before-define
      _interceptApiHeaders(response.headers);

      // * RETURN API RESPONSE
      if (returnFullResponse) return response;
      return response.data;
    })
    .catch(error => {
      if (error.response && error.response.status === 401) {
        const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
        if (CURRENT_USER) {
          if (error.config.headers.GrantType === "refreshToken") {
            removeAccessToken();
            throw error;
          } else {
            // eslint-disable-next-line no-use-before-define
            return retry(
              method,
              url,
              requestBody,
              responseType,
              returnFullResponse,
            );
          }
        } else {
          throw error;
        }
      } else if (
        error.response &&
        error.response.status !== 401 &&
        error.config.headers.GrantType === "refreshToken"
      ) {
        removeAccessToken();
        throw error;
      } else if (
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.response.status === 0
      ) {
        triggerSentryError({ error, level: "info", tag: "Network Error" });
        throw error;
      }
      triggerSentryError({ error, level: "critical", tag: "API Error" });
      console.error("axios error", error);
      throw error;
    });
};

const deleteApi = (url, requestBody) => {
  const method = "delete";
  const headers = makeCommonHeader();
  return makeApiCall(method, url, headers, requestBody);
};

const get = (url, returnFullResponse) => {
  const method = "get";
  const headers = makeCommonHeader();
  return makeApiCall(method, url, headers, null, null, returnFullResponse);
};

const patch = (url, requestBody, returnFullResponse) => {
  const method = "patch";
  const headers = makeCommonHeader();
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const post = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "post";
  const headers = makeCommonHeader();
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const getForFileAsResponse = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "get";
  const headers = makeCommonHeader();
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    "arraybuffer",
    returnFullResponse,
  );
};

const postForFileAsResponse = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "post";
  const headers = makeCommonHeader();
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    "arraybuffer",
    returnFullResponse,
  );
};

const uploadFile = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "post";
  const headers = makeCommonHeader();
  headers["Content-Type"] = "multipart/form-data";
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const postForm = (url, requestBody, returnFullResponse) => {
  const method = "post";
  const headers = makeCommonHeader();
  headers["Content-Type"] = "multipart/form-data";
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const put = (url, requestBody, returnFullResponse) => {
  const method = "put";
  const headers = makeCommonHeader();
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const getTokens = () => {
  const url = `${APIS.AUTH}/initiate-auth`;
  const user = readDataFromLocalStorage("revvsalesUser");
  return post(url, null, "refreshToken", user.refresh_token)
    .then(res => {
      const { access_token: newAccessToken } = res.User;
      user.access_token = newAccessToken;
      writeDataToLocalStorage("revvsalesUser", user);
      setCookie("user", JSON.stringify(user));
      return res.User;
    })
    .catch(err => {
      console.debug(err);
    });
};

const retry = (method, url, requestBody, responseType, returnFullResponse) => {
  if (!fetchTokenPromise) {
    fetchTokenPromise = getTokens();
  }
  return fetchTokenPromise
    .then(() => {
      fetchTokenPromise = null;
      const headers = makeCommonHeader();
      return makeApiCall(
        method,
        url,
        headers,
        requestBody,
        responseType,
        returnFullResponse,
      );
    })
    .catch(err => {
      fetchTokenPromise = null;
      throw err;
    });
};

const _monitorBillingPlanChange = async headers => {
  if (!PLAN_HASH) {
    writeDataToLocalStorage("planHash", (headers && headers.plan_hash) || "");
    PLAN_HASH = readDataFromLocalStorage("planHash");
    return true;
  }

  if (PLAN_HASH !== headers.plan_hash) {
    // Update the new plan hash to the const key
    writeDataToLocalStorage("planHash", (headers && headers.plan_hash) || "");
    PLAN_HASH = readDataFromLocalStorage("planHash");

    try {
      const userFeatures = await get(`${APIS.USER_FEATURES}`);
      const userSignUpInfo = await get(`${APIS.AUTH}/signup_info`);
      const promiseArray = await Promise.all([userFeatures, userSignUpInfo])
        .then(res => {
          writeDataToLocalStorage("revvsalesUserFeatures", res[0].UserFeatures);
          writeDataToLocalStorage("userSignUpInfo", res[1].SignupInfo);
          store.dispatch({
            type: "BILLING_BAND_NOTIFY_ACTION",
            payload: true,
          });
        })
        .catch(error => {
          console.error("api/index.js => _monitorBillingPlanChange", error);
        });
      return promiseArray;
    } catch (error) {
      console.error(`api/index.js => _monitorBillingPlanChange => ${error}`);
      throw error;
    }
  }
  return true;
};

const _monitorAppVersionBump = headers => {
  if (!APP_VERSION) {
    APP_VERSION = headers.fe_version || null;
    window.appVersion = APP_VERSION;
    return true;
  }

  if (headers.fe_version && APP_VERSION !== headers.fe_version) {
    // Check if its a major release
    const MAJOR_VERSION_IN_APP = APP_VERSION.split(".")[0];
    const MAJOR_VERSION_IN_API = headers.fe_version.split(".")[0];
    const isMajorRelease = MAJOR_VERSION_IN_API > MAJOR_VERSION_IN_APP;

    // Update the new app version to the const key
    APP_VERSION = headers.fe_version;
    window.appVersion = APP_VERSION;

    // DISPATCH A REDUX EVENT TO DISPLAY THE NEW APP NOTIFICATION COMPONENT
    store.dispatch({
      type: "TRIGGER_NEW_APP_NOTIFICATION",
      payload: { newVersionFound: true, isMajorRelease },
    });
    return true;
  }
  return true;
};

const _interceptApiHeaders = headers => {
  _monitorBillingPlanChange(headers);
  _monitorAppVersionBump(headers);
};

export {
  deleteApi,
  get,
  patch,
  post,
  put,
  postForm,
  uploadFile,
  postForFileAsResponse,
  getForFileAsResponse,
};



// WEBPACK FOOTER //
// ./src/api/index.js